import { Component, useEffect } from 'react'
import { formatUrl, isPatron } from '../util/helpers'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import AlternateArtButton from './AlternateArtButton'
import CardImage from './CardImage'
import CardLabel from './CardLabel'
import CardPrices from './CardPrices'
import CardSalt from './CardSalt'
import ClipboardButton from './ClipboardButton'
import LazyLoad from 'react-lazyload'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import Spinner from './Spinner'
import { addCard } from '../store/cards'
import bannedImg from '../assets/banned.png'
import classNames from 'classnames'
import styles from '../styles/Card.module.scss'
import unofficialImg from '../assets/unofficial.png'
import useDarkMode from 'use-dark-mode'

const LinkComponent = ({ children, url }) => {
  return url ? <Link to={url}>{children}</Link> : <>{children}</>
}

LinkComponent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  url: PropTypes.string,
}

const MySpinner = () => (
  <div className={styles.spinnerContainer}>
    <Spinner className={styles.spinner} />
  </div>
)

const Card = (props) => {
  const dispatch = useDispatch()
  const keys = props.cards
    ? props.cards.map(({ url }) => url)
    : [props.sanitized_wo || formatUrl(props.name)]
  const selectors = useSelector(
    (state) => keys.map((key) => state.cards[key]),
    shallowEqual
  )
  const profile = useSelector((state) => state.firebase.profile)
  const cards = selectors.map(
    (cardSelector) => cardSelector || { loading: true }
  )

  // Fields from first card
  const { banned, error, is_new, layout, names, prices, salt, unofficial } =
    cards[0]

  // Fields from both cards
  const image_uris =
    profile?.alternateArt && profile?.alternateArt[cards[0]?.url]
      ? profile?.alternateArt[cards[0]?.url] // Alternate art
      : props?.cards && cards[0].image_uris?.length === 1
      ? cards.map(({ image_uri }) => image_uri)
      : cards[0].image_uris
  const loading = cards.some(({ loading }) => loading)

  // Fetch card data on
  useEffect(() => {
    keys.map((key, i) => {
      if (selectors[i] === undefined) {
        dispatch(addCard(key))
      }
    })
  }, [selectors])

  return (
    <>
      {banned ? (
        <div className={styles.banned}>
          <img alt='Banned' src={bannedImg} />
        </div>
      ) : unofficial ? (
        <div className={styles.banned}>
          <img alt='Unofficial' src={unofficialImg} />
        </div>
      ) : null}
      <div
        className={classNames(styles.imageContainer, { [styles.new]: is_new })}
        style={{ height: 340 }}
      >
        <ClipboardButton
          cardProps={cards[0]}
          layout={layout}
          name={props.name}
          names={names}
        />
        {isPatron(profile) && <AlternateArtButton cardProps={cards[0]} />}
        <LinkComponent url={props.url}>
          {error ? (
            <div>{error}</div>
          ) : (
            <CardImage
              height={340}
              isLoading={loading}
              isNew={is_new}
              name={props.name}
              srcs={image_uris}
            />
          )}
        </LinkComponent>
        <CardSalt salt={salt} />
      </div>
      <CardPrices prices={prices} />
    </>
  )
}

Card.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.element.isRequired,
  ]),
  name: PropTypes.string,
  url: PropTypes.string,
}

const CardContainer = (props) => {
  const {
    background,
    hideName,
    label,
    name,
    num_decks,
    potential_decks,
    synergy,
  } = props
  const darkMode = useDarkMode(false)

  if (!(props.url || props.name) || props.hasError) {
    return <MySpinner />
  }
  return (
    <div
      className={classNames(styles.container, {
        [styles.dark]: darkMode.value,
      })}
      style={props.style}
    >
      <LazyLoad
        className={classNames(styles.image, {
          [styles.hideLabel]: props.hideLabel,
          [styles.hideName]: props.hideName,
        })}
        once
      >
        <Card {...props} />
      </LazyLoad>
      <div className={classNames({ [styles.background]: background })}>
        {!hideName && <div className={styles.name}>{name}</div>}
        <CardLabel
          label={label}
          num_decks={num_decks}
          potential_decks={potential_decks}
          synergy={synergy}
        />
      </div>
    </div>
  )
}

class CardErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.error(error.message)
  }

  render() {
    return <CardContainer {...this.props} hasError={this.state.hasError} />
  }
}

export default CardErrorBoundary
